import {
  PinwheelFlowPageResultTypenames,
  Query,
} from '@varo/graphql-schema-types';
import { router, useLocalSearchParams, usePathname } from 'expo-router';
import { useCallback } from 'react';
import PinwheelFlow from 'services/graphql/queries/pinwheel/PinwheelFlow.query';
import { PINWHEEL_LAUNCH_ERROR } from 'shared/constants/pinWheel';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useDirectDepositGateway } from 'shared/hooks/pinwheel/useDirectDepositGateway/useDirectDepositGateway';
import { AbortError } from 'shared/hooks/useCancellableMutation/useCancellableMutation';
import useCancellableQuery from 'shared/hooks/useCancellableQuery/useCancellableQuery';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { isNotNil } from 'shared/utils/isNotNil/isNotNil';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';
import { CombinedError, OperationContext, RequestPolicy, useQuery } from 'urql';

interface PinwheelFlowPageInput {
  requestPolicy?: RequestPolicy;
  pause?: boolean;
}

interface RefetchPinwheelFlowInput {
  refetchPolicy?: RequestPolicy;
  hasPinwheelErrorEvent?: boolean;
  success?: boolean;
}

interface PinwheelFlowPageResult {
  data?: Query;
  error?: CombinedError;
  isAborted?: boolean;
}

type ExecutePinwheelFlowQuery = (
  variables: Record<string, never>,
  context?: Partial<OperationContext>,
) => Promise<Query>;

const LOG_CONTEXT = {
  featureArea: FeatureArea.DIRECT_DEPOSIT,
  responsibleTeam: ResponsibleTeam.CORE_BANKING,
  severity: ErrorSeverity.LOW,
};

const runPinwheelFlowQuery = async (
  executePinwheelFlowQuery: ExecutePinwheelFlowQuery,
  requestPolicy: RequestPolicy = 'cache-first',
): Promise<PinwheelFlowPageResult> => {
  try {
    const data = await executePinwheelFlowQuery({}, { requestPolicy });

    return { data };
  } catch (error) {
    if (error instanceof AbortError) {
      return { isAborted: true };
    }

    if (error instanceof CombinedError) {
      return { error };
    }

    throw error;
  }
};

const useDirectDepositManagementFlow = ({
  pause = false,
  requestPolicy = 'cache-first',
}: PinwheelFlowPageInput = {}) => {
  const [executePinwheelFlowQuery] = useCancellableQuery<
    Query,
    Record<string, never>
  >(PinwheelFlow);
  const [executeDdManagementRefetchQuery] = useCancellableQuery<
    Query,
    Record<string, never>
  >(PinwheelFlow);

  const [{ data, error, fetching }] = useQuery<Query>({
    query: PinwheelFlow,
    pause,
    requestPolicy,
  });

  /**
   * Asynchronous fetch function using useCancellableQuery
   * This lets you `await fetchPinWheelFlowPageData()` if needed.
   */
  const fetchPinWheelFlowPageData = useCallback(
    (requestPolicy?: RequestPolicy) =>
      runPinwheelFlowQuery(executePinwheelFlowQuery, requestPolicy),
    [executePinwheelFlowQuery],
  );

  const { standAloneEntry } = useLocalSearchParams<{
    standAloneEntry: string;
  }>();

  const { directDepositGatewayFlag } = useDirectDepositGateway();
  const pathname = usePathname();
  const refetchDdManagementFlow = useCallback(
    ({
      refetchPolicy = 'cache-and-network',
      hasPinwheelErrorEvent,
      success,
    }: RefetchPinwheelFlowInput) => {
      if (success || hasPinwheelErrorEvent) {
        void runPinwheelFlowQuery(
          executeDdManagementRefetchQuery,
          refetchPolicy,
        ).then((result) => {
          if (result.isAborted) {
            return;
          }

          if (result.error) {
            logErrorWithContext(result.error, LOG_CONTEXT);
          }

          // The Benefits screen is reused in both the Move Money and Get Early Payday flows.
          //standAloneEntry identifies whether Benefits is the entry screen.
          //- false: Benefits was reached from Setup Intro, so the CTA navigates back.
          //- true: Benefits is the entry screen, so the CTA navigates to Setup Intro.
          //After a successful Pinwheel flow, dismiss the Setup screen and navigate to the DD Management screen.
          //This prevents the Setup screen from appearing again if the user navigates back from DD Management.
          if (
            directDepositGatewayFlag &&
            pathname === '/direct-deposit-setup'
          ) {
            router.dismiss();
            if (standAloneEntry) {
              router.replace(
                '/move-money/direct-deposit/direct-deposit-management',
              );
            } else {
              router.push(
                '/move-money/direct-deposit/direct-deposit-management',
              );
            }
          } else {
            router.push('/move-money/direct-deposit/direct-deposit-management');
          }
        });
      }
    },
    [
      executeDdManagementRefetchQuery,
      standAloneEntry,
      directDepositGatewayFlag,
      pathname,
    ],
  );

  const handleDdManagementLinkTokenFailure = () =>
    router.push(`/move-money/direct-deposit?name=${PINWHEEL_LAUNCH_ERROR}`);

  const pinwheelFlowPage = data?.pinwheelFlowPage;
  const hasError =
    pinwheelFlowPage?.__typename ===
    PinwheelFlowPageResultTypenames.PinwheelFlowPageError;

  const pinwheelFlowPageError = hasError || isNotNil(error);

  const shouldLaunchPinwheelSDK =
    pinwheelFlowPage?.__typename ===
    PinwheelFlowPageResultTypenames.DdPinwheelSetupPage;

  const isDDManagementPage =
    pinwheelFlowPage?.__typename ===
    PinwheelFlowPageResultTypenames.DdManagementPage;

  const ddManagementPage = isDDManagementPage ? pinwheelFlowPage : undefined;
  const ddManagementErrorPage = hasError ? pinwheelFlowPage : undefined;

  const { defaultTitle, defaultMessage, retryButtonTitle } =
    useDefaultErrorStrings();

  const errorModel = pinwheelFlowPageError
    ? {
        screenType: ErrorScreenType.Error,
        messageHeadline: defaultTitle,
        messageBody: defaultMessage,
        buttons: {
          primaryButton: {
            title: retryButtonTitle,
            onPress: () => {
              void fetchPinWheelFlowPageData();
            },
          },
        },
      }
    : null;

  return {
    ddManagementPage,
    errorModel,
    fetchPinWheelFlowPageData,
    refetchDdManagementFlow,
    pinwheelFlowPageError,
    shouldLaunchPinwheelSDK,
    shouldRedirectToDDManagementPage: isDDManagementPage,
    isLoading: fetching,
    ddManagementErrorPage,
    error,
    handleDdManagementLinkTokenFailure,
  };
};

export default useDirectDepositManagementFlow;
